/**
 * Predictive search.
 *
 * Suggestions come from Shopify's Predictive Search API, rendered by
 * sections/predictive-search.liquid. The theme keeps no index of its own and
 * does no client-side matching, so what the panel shows is always what a real
 * search would return.
 *
 * Requests are debounced and every in-flight request is aborted when a newer
 * keystroke arrives, so a fast typist cannot end up looking at results for a
 * prefix they have already moved past.
 */
(function () {
  'use strict';

  const { utils, a11y, config } = window.Theme;

  const MIN_LENGTH = 2;
  const DEBOUNCE_MS = 250;

  if (customElements.get('predictive-search')) return;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-psearch-input]');
      this.resultsEl = this.querySelector('[data-psearch-results]');
      this.statusEl = this.querySelector('[data-psearch-status]');
      this.resetButton = this.querySelector('[data-psearch-reset]');
      this.panel = this.querySelector('.psearch__panel');

      this.controller = null;
      this.cache = new Map();
      this.activeIndex = -1;

      this.limit = parseInt(this.dataset.limit, 10) || 5;
      this.types = this.dataset.types || 'product';

      this.onInput = utils.debounce(() => this.search(), DEBOUNCE_MS);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onDocumentKeydown = this.onDocumentKeydown.bind(this);

      this.input?.addEventListener('input', () => {
        this.toggleReset();
        this.onInput();
      });
      this.input?.addEventListener('keydown', this.onKeydown);

      this.querySelector('[data-psearch-reset]')?.addEventListener('click', () => this.clear());
      this.querySelectorAll('[data-psearch-close], [data-psearch-overlay]').forEach((el) => {
        el.addEventListener('click', () => this.close());
      });

      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      // The header icon is a plain link so it still works without JavaScript.
      // Now that the component is alive it becomes a button that opens this
      // panel instead of navigating away.
      document.querySelectorAll('[data-search-toggle]').forEach((toggle) => {
        toggle.setAttribute('role', 'button');
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);
      this.controller?.abort();
      if (this.dataset.open === 'true') this.close(false);
    }

    /* ------------------------------------------------------------------ */
    /* Open / close                                                        */
    /* ------------------------------------------------------------------ */

    onDocumentClick(event) {
      const toggle = event.target.closest('[data-search-toggle]');
      if (!toggle) return;
      event.preventDefault();
      this.open();
    }

    onDocumentKeydown(event) {
      if (event.key === 'Escape' && this.dataset.open === 'true') {
        event.preventDefault();
        this.close();
      }
    }

    open() {
      if (this.dataset.open === 'true') return;

      this.hidden = false;
      // Force a layout pass synchronously rather than through
      // requestAnimationFrame, which never fires in a background tab and
      // would leave the panel present but invisible.
      void this.offsetWidth;
      this.dataset.open = 'true';

      document.querySelectorAll('[data-search-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'true'));
      a11y.lockScroll();
      a11y.trapFocus(this, this.input || this);
      this.input?.focus({ preventScroll: true });
      this.input?.select();
    }

    close(returnFocus = true) {
      if (this.dataset.open !== 'true') return;

      this.dataset.open = 'false';
      document.querySelectorAll('[data-search-toggle]').forEach((t) => t.setAttribute('aria-expanded', 'false'));
      a11y.removeTrapFocus(returnFocus);
      a11y.unlockScroll();
      this.controller?.abort();

      const finish = () => {
        if (this.dataset.open !== 'true') this.hidden = true;
      };
      this.addEventListener('transitionend', finish, { once: true });
      setTimeout(finish, 400);
    }

    /* ------------------------------------------------------------------ */
    /* Searching                                                           */
    /* ------------------------------------------------------------------ */

    async search() {
      const term = (this.input?.value || '').trim();

      if (term.length < MIN_LENGTH) {
        this.controller?.abort();
        this.renderResults('');
        return;
      }

      if (this.cache.has(term)) {
        this.renderResults(this.cache.get(term));
        return;
      }

      // Abort the previous request rather than racing it: otherwise a slow
      // response for "sn" can land after a fast one for "snowboard" and
      // overwrite the newer, correct results.
      this.controller?.abort();
      this.controller = new AbortController();

      this.dataset.loading = 'true';

      const url = new URL(config.routes.predictiveSearch, window.location.origin);
      url.searchParams.set('q', term);
      url.searchParams.set('resources[type]', this.types);
      url.searchParams.set('resources[limit]', String(this.limit));
      url.searchParams.set('section_id', 'predictive-search');

      try {
        const response = await fetch(url.toString(), { signal: this.controller.signal });
        if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

        const html = await response.text();
        const parsed = utils.parseSection(html, '.shopify-section') || utils.parseSection(html, 'body');
        const markup = parsed ? parsed.innerHTML : '';

        this.cache.set(term, markup);
        this.renderResults(markup);
      } catch (error) {
        if (error.name === 'AbortError') return;
        // A failed suggestion lookup is not worth an error message: the real
        // search form underneath still works, so the panel simply shows
        // nothing extra.
        this.renderResults('');
      } finally {
        delete this.dataset.loading;
      }
    }

    renderResults(markup) {
      if (!this.resultsEl) return;

      this.resultsEl.innerHTML = markup;
      this.activeIndex = -1;
      this.items = Array.from(this.resultsEl.querySelectorAll('[data-psearch-item]'));

      const hasResults = this.items.length > 0;
      this.input?.setAttribute('aria-expanded', String(hasResults));
      this.toggleAttribute('data-has-results', hasResults);

      if (this.statusEl) {
        this.statusEl.textContent = hasResults
          ? (window.Theme.strings.searchResultsCount || '').replace('[count]', String(this.items.length))
          : '';
      }
    }

    /* ------------------------------------------------------------------ */
    /* Keyboard                                                            */
    /* ------------------------------------------------------------------ */

    onKeydown(event) {
      const items = this.items || [];

      switch (event.key) {
        case 'ArrowDown':
          if (!items.length) return;
          event.preventDefault();
          this.moveActive(this.activeIndex + 1);
          break;

        case 'ArrowUp':
          if (!items.length) return;
          event.preventDefault();
          this.moveActive(this.activeIndex - 1);
          break;

        case 'Enter':
          // Enter with a highlighted suggestion follows it; Enter with nothing
          // highlighted submits the form and goes to the full results page,
          // which is what a visitor who ignored the list expects.
          if (this.activeIndex >= 0 && items[this.activeIndex]) {
            event.preventDefault();
            items[this.activeIndex].click();
          }
          break;

        case 'Escape':
          event.preventDefault();
          // The document-level handler closes the panel on Escape. When there
          // is a term to clear, this keypress has already been spent doing
          // that, so it must not also reach the document and close the panel
          // in the same stroke.
          event.stopPropagation();
          if (this.input?.value) this.clear();
          else this.close();
          break;

        default:
          break;
      }
    }

    moveActive(next) {
      const items = this.items || [];
      if (!items.length) return;

      items.forEach((item) => {
        item.removeAttribute('aria-selected');
        item.classList.remove('is-active');
      });

      this.activeIndex = ((next % items.length) + items.length) % items.length;

      const current = items[this.activeIndex];
      current.setAttribute('aria-selected', 'true');
      current.classList.add('is-active');
      current.scrollIntoView({ block: 'nearest' });

      // The input keeps focus so typing continues to work; the highlighted
      // option is announced through aria-activedescendant instead.
      if (!current.id) current.id = `psearch-option-${this.activeIndex}`;
      this.input?.setAttribute('aria-activedescendant', current.id);
    }

    /* ------------------------------------------------------------------ */
    /* Helpers                                                             */
    /* ------------------------------------------------------------------ */

    clear() {
      if (!this.input) return;
      this.input.value = '';
      this.input.focus();
      this.toggleReset();
      this.controller?.abort();
      this.renderResults('');
    }

    toggleReset() {
      if (this.resetButton) this.resetButton.hidden = !(this.input && this.input.value.length > 0);
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
})();
